import React, { forwardRef } from 'react';
import classNames from 'classnames';
import './KSInputField.scss';

export type KSInputFieldProps = {
  /** The label of the input field */
  label?: string;
  /** The value of the input */
  value: string;
  /** The placeholder text */
  placeholder?: string;
  /** The onChange event of the input */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** The onBlur event of the input */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** The onFocus event of the input */
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Custom class name */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Error message */
  errorMessage?: string;
  /** Input type */
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
  /** Required field */
  required?: boolean;
  /** Read only */
  readOnly?: boolean;
  /** Max length */
  maxLength?: number;
  /** The onKeyDown event of the input */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

const KSInputField = forwardRef<HTMLInputElement, KSInputFieldProps>(
  (
    {
      label,
      value,
      placeholder,
      onChange,
      onBlur,
      onFocus,
      className = '',
      disabled = false,
      error = false,
      errorMessage,
      type = 'text',
      required = false,
      readOnly = false,
      maxLength,
      onKeyDown,
    },
    ref
  ) => {
    return (
      <div
        className={classNames('ks-input-field', {
          [className]: className,
          'ks-input-field--error': error,
          'ks-input-field--disabled': disabled,
        })}
      >
        {label && (
          <label className="ks-input-field__label" htmlFor={label}>
            {label}
          </label>
        )}
        <div className="ks-input-field__wrapper">
          <input
            ref={ref}
            type={type}
            value={value}
            placeholder={placeholder}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            disabled={disabled}
            required={required}
            readOnly={readOnly}
            maxLength={maxLength}
            className={classNames('ks-input-field__input', {
              'ks-input-field__input--error': error,
            })}
          />
          {error && errorMessage && <span className="ks-input-field__error-message">{errorMessage}</span>}
        </div>
      </div>
    );
  }
);

KSInputField.displayName = 'KSInputField';

export default KSInputField;
